import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useMediaQuery } from "context/media/MediaContext";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import {
  Panel,
  PanelHeader,
  TextField,
  Button,
  Spinner,
  Container,
} from "components";
import {
  Col,
  ElementWrapper,
  Row,
  StyledFormWrapper,
} from "components/BaseComponents/Flex";
import { IFirstPasswordChangeAdminRequest } from "models/Password/password.model";
import { yupResolver } from "@hookform/resolvers/yup";
import { firstPassChangeAdminSubmitValidationSchema } from "validations/password.validation";
import { firstPasswordChangeAdminAsync } from "services/password/password.service";
import { TokenInclude } from "helpers/GetToken";
import Alert from "components/BaseComponents/Alert/Alert";
import { useAppDispatch } from "redux/hooks";
import {
  setFirstPassChangeAdmin,
  setFirstPassChangeAdminGuv,
} from "redux/slices/password-slices/PasswordRefreshSlice";
import { ResetLogin } from "redux/slices/LoginSlice";
import { resetOtp } from "redux/slices/OtpSlice";
import { requestDataDefaultValues } from "./PassForm.constant";

const FirstPasswordChangeAdmin: FC<{}> = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const token = TokenInclude();

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>(t("success_add_message").toString());

  const {
    handleSubmit,
    control,
    errors,
    reset,
    formState: { isSubmitting },
  } = useForm<IFirstPasswordChangeAdminRequest>({
    defaultValues: requestDataDefaultValues,
    resolver: yupResolver(firstPassChangeAdminSubmitValidationSchema(t)),
  });
  const onSubmit = async (
    data: IFirstPasswordChangeAdminRequest
  ): Promise<void> => {
    const { newPassword } = data;

    const retVal = await firstPasswordChangeAdminAsync(token, newPassword!);
    if (retVal.confirm) {
      dispatch(setFirstPassChangeAdmin(true));
      navigate("/admin-security-photo");
    } else {
      const errMessage: any = retVal;
      setIsAddError(true);
      setTxt(errMessage);
      return;
    }
    reset();
  };

  const NavigateBackPage = () => {
    dispatch(ResetLogin());
    dispatch(resetOtp());
    dispatch(setFirstPassChangeAdmin(false));
    dispatch(setFirstPassChangeAdminGuv(false));
    navigate("/");
  };

  return (
    <Container>
      <ElementWrapper
        minHeight={isMobile ? "calc(100vh - 118px)" : "calc(100vh - 137px)"}
        padding='48px 0 0 0'
        width='872px'
      >
        <Panel width='646px' marginTop='0'>
          {(isAddSuccess || isAddError) && (
            <Alert
              alertType={isAddSuccess ? "Success" : "Error"}
              duration={6000}
              isVisible={isAddSuccess || isAddError}
              message={txt}
              setIsAddSuccess={setIsAddSuccess}
              setIsAddError={setIsAddError}
            />
          )}
          <Spinner show={isSubmitting} />
          <PanelHeader title={t("password_refresh")} />

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <StyledFormWrapper>
              <PanelHeader />
              <Row
                alignItems='flex-start'
                direction={isMobile ? "column-reverse" : "row"}
                noMargin
              >
                <Row width='100%'>
                  <Col>
                    <TextField
                      control={control}
                      error={errors.newPassword?.message}
                      label={t("new_password").toString()}
                      maxLength={6}
                      name='newPassword'
                      type='masked'
                    />
                  </Col>
                </Row>
                <Row width='100%'>
                  <Col>
                    <TextField
                      control={control}
                      error={errors.newPasswordAgain?.message}
                      label={t("new_password_repeat").toString()}
                      maxLength={6}
                      name='newPasswordAgain'
                      type='masked'
                    />
                  </Col>
                </Row>
              </Row>
            </StyledFormWrapper>

            <Row
              backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
              justifyContent='flex-end'
              isResponsive
              noMargin
            >
              <Col>
                <Button
                  type='button'
                  width={isMobile ? "100%" : "100px"}
                  text={`← ${t("back")}`}
                  onClick={NavigateBackPage}
                  color='light'
                  disabled={isAddSuccess}
                />
              </Col>
              <Col offSet={22} size={0.5}>
                <Button text={t("refresh").toString()} type='submit' />
              </Col>
            </Row>
          </form>
        </Panel>
      </ElementWrapper>
    </Container>
  );
};

export default FirstPasswordChangeAdmin;
